import type { ComponentProps, ReactNode } from 'react';

/** Systems a previewable link can point at. Values double as the visible source label. */
enum PreviewSource {
  JIRA = 'Jira',
  AZURE_DEVOPS = 'Azure DevOps',
  BLOOMFILTER = 'Bloomfilter',
}

/** Kinds of item a preview can describe. Values double as the visible label. */
enum PreviewKind {
  ISSUE = 'Issue',
  WORK_ITEM = 'Work item',
  DASHBOARD = 'Dashboard',
  INITIATIVE = 'Initiative',
  PAGE = 'Page',
}

/** A link recognised as previewable, parsed from its href. */
interface PreviewTarget {
  source: PreviewSource;
  kind: PreviewKind;
  /** Fallback title shown before (or instead of) fetched details. */
  title: string;
  /** Issue key, work-item id, dashboard id or initiative id, when the URL carries one. */
  id?: string;
  /** Portfolio id parsed from an initiative URL. */
  portfolioId?: string;
  url: URL;
  /**
   * True when the host is known to belong to the source (Jira Cloud, Azure DevOps
   * Services, the org's Jira domain, or the app itself). Links on other hosts only
   * match by URL shape — e.g. Jira Server/Data Center or Azure DevOps Server — so
   * they get a preview only once a lookup confirms the item exists.
   */
  knownHost: boolean;
}

/** Display-ready details for a preview card; every field is optional because sources vary. */
interface CachedDetails {
  title?: string;
  description?: string;
  type?: string;
  assignee?: string;
  /** True only when the source payload carries an assignee field, so a missing field never reads as "Unassigned". */
  assigneeKnown?: boolean;
  /** The task's current board column, e.g. "In Progress". */
  status?: string;
  epicKey?: string;
  epicName?: string;
  /** True when these details came from a record that matched the link, rather than an empty fallback. */
  matched?: boolean;
}

/**
 * Loose shape of a task as it appears in task search results and cached task
 * lists. Endpoints disagree on field presence and on `epic`'s shape, so all
 * fields are optional and `epic` is narrowed at read time.
 */
interface PreviewTaskRecord {
  name?: string;
  description?: string;
  raw_type_label?: string | null;
  type?: string;
  assignee_name?: string | null;
  current_section?: string | null;
  epic?: unknown;
  epic_key?: string | null;
  url?: string;
}

/** What {@link useLinkPreviewDetails} returns to the preview card. */
interface LinkPreviewDetailsResult {
  details: CachedDetails;
  isFetching: boolean;
}

/** Props for `AgentLinkPreview`: every anchor prop, forwarded to the rendered link. */
type AgentLinkPreviewProps = ComponentProps<'a'> & { children?: ReactNode };

export { PreviewKind, PreviewSource };
export type { AgentLinkPreviewProps, CachedDetails, LinkPreviewDetailsResult, PreviewTarget, PreviewTaskRecord };
